import { Theme, alpha, PaletteColor } from '@mui/material';
import { LetterCategorySeverity } from '../types/letters';
import type { PdfTone } from './pdf/theme';

/**
 * Map a letter category's severity to the right MUI palette tone. Used by
 * every chip, border, and KPI tile across letters & fines surfaces so the
 * colour grammar stays consistent — change here once, propagates everywhere.
 *
 * positive → success (green)   commendations
 * neutral  → info (blue)        notices
 * caution  → warning (amber)    warnings, fines
 * critical → error (red)        terminations
 */
export const severityTone = (theme: Theme, severity?: LetterCategorySeverity | null): PaletteColor => {
  switch (severity) {
    case 'positive': return theme.palette.success;
    case 'neutral':  return theme.palette.info;
    case 'caution':  return theme.palette.warning;
    case 'critical': return theme.palette.error;
    default:         return theme.palette.info;
  }
};

/** The same severity grammar in the PDF kit's tones — for letters and fines printed in reports. */
export const SEVERITY_PDF_TONE: Record<LetterCategorySeverity, PdfTone> = {
  positive: 'success',
  neutral: 'info',
  caution: 'warning',
  critical: 'danger',
};

/**
 * Convenience: chip-style sx for a category severity. Keeps the soft-tinted
 * background + bold tone-coloured label + icon in lock-step with the theme.
 */
export const severityChipSx = (theme: Theme, severity?: LetterCategorySeverity | null) => {
  const tone = severityTone(theme, severity);
  return {
    bgcolor: alpha(tone.main, 0.1),
    color: tone.main,
    fontWeight: 600,
    border: 'none',
    '& .MuiChip-icon': { color: tone.main }
  } as const;
};

/**
 * Payment status pill colouring. Outstanding / partial use the warning tone,
 * paid is success, waived is a neutral grey so it doesn't get confused with
 * a real collection event.
 */
export const paymentStatusSx = (theme: Theme, status?: string | null) => {
  switch (status) {
    case 'paid':
      return { bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main };
    case 'partial':
      return { bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main };
    case 'waived':
      return { bgcolor: alpha(theme.palette.grey[700], 0.1), color: theme.palette.grey[700] };
    case 'unpaid':
    default:
      return { bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main };
  }
};
